import { makeAutoObservable } from 'mobx'
import { HitAnimationCategory, HitAnimationType, PowerUpType, RunPhase, RunPowerUp, RunSnapshot, type RunEndStatsSummary } from './runStateTypes'
import { getLaneCount, hitFreezeDuration, hitReactionDuration, jumpUnlockDuration, lanePositions, maxLifes, obstacleCollisionHideDelay, planePowerUpDuration, shieldCollectCooldown } from '../game/config/constants'
import { SHIELD_BREAK_EVENT } from '../game/models/character/shieldBreakEvent'
import { playMusicInterruption, playSound, playSoundUntilFinished } from '../utils/soundPlayer'
import { playCoinCollectSound, resetCoinCollectSequence } from '../game/audio/coinCollectSound'
import { playCharacterCrashSoundUntilFinished } from '../game/audio/characterCrashSound'
import {
    playCharacterHeartCollectSoundAfterCollect,
    playCharacterLightningSoundAfterActivate,
    playCharacterPlaneSoundAfterCollect,
} from '../game/audio/characterEventSound'
import {
    playCharacterAirSwitchSound,
    playCharacterJumpSound,
    playCharacterLaneSwitchSound,
} from '../game/audio/characterMovementSound'
import type { TrackRegionState } from '../game/models/track/trackRegions'
import { selectCardPowerUp } from '../game/config/cardSelectionConfig'
import { store } from './store'
import { vibrate } from '../lib/vibration'

function clampLaneIndex(laneIndex: number) {
    return Math.max(0, Math.min(getLaneCount() - 1, laneIndex))
}

const initialNextObstacleId = 4
const initialLowerObstacleHitSide = 'left'
const initialUpperObstacleHitSide = 'left'

function emptyRunEndStats(): RunEndStatsSummary {
    return {
        powerUpsCollected: 0,
        batteryPowerUpsCollected: 0,
        shieldPowerUpsCollected: 0,
        planePowerUpsCollected: 0,
        obstacleHits: 0,
        heartPowerUpsCollected: 0,
        coinPowerUpsCollected: 0,
    }
}

function createInitialSnapshot(): RunSnapshot {
    return {
        distance: 0,
        characterWorldZ: 0.2,
        characterVisualPosition: [0, 0, 0],
        currentLevelIndex: 0,
        levelTransitionTimer: 0,
        levelTransitionTunnelZ: null,
        laneIndex: 1,
        laneVisualX: 0,
        laneSwitchDirection: 0,
        laneSwitchTimer: 0,
        isJumping: false,
        jumpTriggerId: 0,
        jumpTimer: 0,
        jumpOffset: 0,
        visualJumpOffset: 0,
        debugCollisionObstacleId: null,
        lifes: 3,
        hitReactionTimer: 0,
        lastHitAnimation: null,
        hitFreezeTimer: 0,
        powerUps: [],
        shieldActive: false,
        batteryTimer: 0,
        planeTimer: 0,
        levelCompleteTimer: 0,
        finishLineZ: null,
        finishLineReached: false,
        runEndStats: emptyRunEndStats(),
        batteryCount: 0,
        snapActive: false,
    }
}

function createInitialTrackRegionState(): TrackRegionState {
    return {
        activeRegion: 1,
        activeTransition: false,
        transitionProgress: 0,
        trackPosition: 0,
        regions: [
            { id: 1, weight: 1 },
            { id: 2, weight: 0 },
            { id: 3, weight: 0 },
            { id: 4, weight: 0 },
        ],
    }
}

export class RunStateStore {
    phase: RunPhase = 'idle'

    levelCompleteCardAnimationComplete = false
    cardEpicEnabled = false
    getGameEndCardPressed = false

    // bumped on every reset/restart so the on-track collectibles know to respawn.
    collectiblesResetToken = 0

    // wall-clock ms of the last successful shield pickup (blocks double-collect HUD races)
    private lastShieldCollectTime = Number.NEGATIVE_INFINITY

    nextObstacleId = initialNextObstacleId
    activeLaneCount = getLaneCount()
    nextLowerObstacleHitSide: 'left' | 'right' = initialLowerObstacleHitSide
    nextUpperObstacleHitSide: 'left' | 'right' = initialUpperObstacleHitSide

    snapshot: RunSnapshot = createInitialSnapshot()
    trackRegionState: TrackRegionState = createInitialTrackRegionState()

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true })
    }

    start() {
        if (this.phase === 'running') return
        this.phase = 'running'
    }

    pause() {
        if (this.phase !== 'running' && this.phase !== 'levelTransition') return
        this.phase = 'paused'
    }

    reset() {
        this.phase = 'idle'
        this.nextObstacleId = initialNextObstacleId
        this.activeLaneCount = getLaneCount()
        this.nextLowerObstacleHitSide = initialLowerObstacleHitSide
        this.nextUpperObstacleHitSide = initialUpperObstacleHitSide
        this.snapshot = createInitialSnapshot()
        this.trackRegionState = createInitialTrackRegionState()
        this.nextPowerUpId = 1
        this.levelCompleteCardAnimationComplete = false
        this.cardEpicEnabled = false
        this.snapshot.batteryCount = 0
        this.getGameEndCardPressed = false
        this.snapshot.snapActive = false
        this.hiddenObstacleIds.clear()
        this.clearPendingObstacleHideTimeouts()
        resetCoinCollectSequence()
        store.powerUpTutorial.reset()
        this.collectiblesResetToken += 1
        this.lastShieldCollectTime = Number.NEGATIVE_INFINITY
    }

    canCollectShield() {
        return performance.now() - this.lastShieldCollectTime >= shieldCollectCooldown * 1000
    }

    syncLaneConfiguration() {
        const laneCount = getLaneCount()

        if (this.activeLaneCount === laneCount) return

        this.activeLaneCount = laneCount

        if (this.phase === 'idle') {
            this.snapshot = createInitialSnapshot()
            return
        }

        const nextLaneX = lanePositions[this.snapshot.laneIndex]
        this.snapshot.laneVisualX = nextLaneX
        this.snapshot.laneSwitchDirection = 0
        this.snapshot.laneSwitchTimer = 0
        this.snapshot.characterVisualPosition = [
            nextLaneX,
            this.snapshot.characterVisualPosition[1],
            this.snapshot.characterVisualPosition[2],
        ]
        this.snapshot.powerUps = this.snapshot.powerUps.map((powerUp) => ({
            ...powerUp,
            laneIndex: clampLaneIndex(powerUp.laneIndex),
        }))
    }

    markLevelCompleteCardAnimationComplete() {
        if (this.phase === 'endCard') {
            this.levelCompleteCardAnimationComplete = true
        }
    }

    enableEpicCard() {
        if (this.phase === 'endCard') {
            this.cardEpicEnabled = true
        }
    }

    setGameEndCardPressed(pressed: boolean) {
        if (this.phase === 'endCard' || !pressed) {
            this.getGameEndCardPressed = pressed
        }
    }

    setCharacterVisualPosition(position: [number, number, number]) {
        this.snapshot.characterVisualPosition = position
    }

    setCharacterWorldZ(characterWorldZ: number) {
        this.snapshot.characterWorldZ = characterWorldZ
    }

    setTrackRegionState(trackRegionState: TrackRegionState) {
        this.trackRegionState = trackRegionState
    }

    createObstacleId() {
        const id = `obstacle-${this.nextObstacleId}`
        this.nextObstacleId += 1
        return id
    }

    setPhase(phase: RunPhase) {
        this.phase = phase
        if (phase !== 'levelComplete') {
            this.levelCompleteCardAnimationComplete = false
        }
        if (phase !== 'endCard') {
            this.cardEpicEnabled = false
            this.getGameEndCardPressed = false
        }
    }

    completeRun() {
        this.levelCompleteCardAnimationComplete = false
        this.cardEpicEnabled = false
        this.getGameEndCardPressed = false
        this.phase = 'levelComplete'
        this.snapshot.levelCompleteTimer = 0
    }

    setLevelTransitionTimer(timer: number) {
        this.snapshot.levelTransitionTimer = timer
    }

    setLevelCompleteTimer(timer: number) {
        this.snapshot.levelCompleteTimer = timer
    }

    completeLevelComplete() {
        this.phase = 'endCard'
    }

    startLevelTransition(tunnelZ: number) {
        this.snapshot.levelTransitionTimer = 0
        this.snapshot.levelTransitionTunnelZ = tunnelZ
        this.levelCompleteCardAnimationComplete = false
        this.cardEpicEnabled = false
        this.getGameEndCardPressed = false
        this.phase = 'levelTransition'
    }

    spawnFinishLine(z: number) {
        this.snapshot.finishLineZ = z
        this.snapshot.finishLineReached = false
    }

    completeRunAfterFinishLine() {
        this.snapshot.finishLineReached = true
        this.completeRun()
    }

    moveLane(direction: -1 | 1) {
        const currentLaneIndex = this.snapshot.laneIndex
        const nextLaneIndex = Math.max(
            0,
            Math.min(getLaneCount() - 1, currentLaneIndex + direction),
        )

        if (nextLaneIndex === currentLaneIndex) return

        this.snapshot.laneIndex = nextLaneIndex
        this.snapshot.laneSwitchDirection = direction
        this.snapshot.laneSwitchTimer = 0
        if (this.snapshot.isJumping) {
            playCharacterAirSwitchSound()
        } else {
            playCharacterLaneSwitchSound()
        }
    }

    setLaneVisualX(x: number) {
        this.snapshot.laneVisualX = x
    }

    setLaneSwitchTimer(timer: number) {
        this.snapshot.laneSwitchTimer = timer
    }

    clearLaneSwitchDirection() {
        this.snapshot.laneSwitchDirection = 0
    }

    startJump() {
        if (this.phase !== 'running' && this.phase !== 'levelTransition') return
        if (this.snapshot.hitReactionTimer > 0) return
        if (this.snapshot.hitFreezeTimer > 0) return
        if (this.snapshot.isJumping && this.snapshot.jumpTimer < jumpUnlockDuration) return

        this.snapshot.isJumping = true
        this.snapshot.jumpTimer = 0
        this.snapshot.jumpTriggerId += 1
        playCharacterJumpSound()
    }

    setJumpState({
        isJumping,
        jumpTimer,
        jumpOffset,
    }: {
        isJumping: boolean
        jumpTimer: number
        jumpOffset: number
    }) {
        this.snapshot.isJumping = isJumping
        this.snapshot.jumpTimer = jumpTimer
        this.snapshot.jumpOffset = jumpOffset
    }

    endJump() {
        this.snapshot.isJumping = false
        this.snapshot.jumpTimer = 0
        this.snapshot.jumpOffset = 0
    }

    setDebugCollisionObstacleId(id: string | null) {
        this.snapshot.debugCollisionObstacleId = id
    }

    setHitReactionTimer(timer: number) {
        this.snapshot.hitReactionTimer = timer
    }

    resolveNextHitAnimation(hitAnimationCategory: HitAnimationCategory): HitAnimationType {
        if (hitAnimationCategory === 'lowerObstacle') {
            const hitAnimation =
                this.nextLowerObstacleHitSide === 'left'
                    ? 'lowerObstacleLeft'
                    : 'lowerObstacleRight'
            this.nextLowerObstacleHitSide =
                this.nextLowerObstacleHitSide === 'left' ? 'right' : 'left'
            return hitAnimation
        }

        const hitAnimation =
            this.nextUpperObstacleHitSide === 'left'
                ? 'upperObstacleLeft'
                : 'upperObstacleRight'
        this.nextUpperObstacleHitSide =
            this.nextUpperObstacleHitSide === 'left' ? 'right' : 'left'
        return hitAnimation
    }

    applyObstacleHit(
        obstacleId: string,
        hitAnimationCategory: HitAnimationCategory,
    ) {
        if (this.snapshot.hitReactionTimer > 0) return
        vibrate(40)

        const nextLifes = Math.max(0, this.snapshot.lifes - 1)
        const hitAnimation = this.resolveNextHitAnimation(hitAnimationCategory)

        this.snapshot.lifes = nextLifes
        this.snapshot.hitReactionTimer = hitReactionDuration
        this.snapshot.hitFreezeTimer = hitFreezeDuration
        this.snapshot.debugCollisionObstacleId = obstacleId
        this.snapshot.lastHitAnimation = hitAnimation

        this.snapshot.runEndStats = {
            ...this.snapshot.runEndStats,
            obstacleHits: this.snapshot.runEndStats.obstacleHits + 1,
        }
        this.hideObstacle(obstacleId, obstacleCollisionHideDelay)
        void playCharacterCrashSoundUntilFinished(nextLifes <= 0).then(() => {
            playSound('heartLost')
        })

        if (nextLifes <= 0) {
            vibrate(400)
            this.levelCompleteCardAnimationComplete = false
            this.cardEpicEnabled = false
            this.getGameEndCardPressed = false
            this.phase = 'gameOver'
        }
    }

    setHitFreezeTimer(timer: number) {
        this.snapshot.hitFreezeTimer = timer
    }

    nextPowerUpId = 1

    createPowerUpId() {
        const id = `powerup-${this.nextPowerUpId}`
        this.nextPowerUpId += 1
        return id
    }

    collectPowerUp(id: string) {
        const powerUp = this.snapshot.powerUps.find((p) => p.id === id)
        if (!powerUp) return
        if (powerUp.collectedState !== 'active') return

        this.snapshot.powerUps = this.snapshot.powerUps.map((p) =>
            p.id === id
                ? { ...p, collectedState: 'collected' as const, collectTimer: 0 }
                : p,
        )

        this.activatePowerUp(powerUp.type)
    }

    tickCollectTimers(delta: number) {
        this.snapshot.powerUps = this.snapshot.powerUps.map((p) => {
            if (p.collectedState !== 'collected') return p
            return { ...p, collectTimer: (p.collectTimer ?? 0) + delta }
        })
    }

    removePowerUp(id: string) {
        console.trace('removePowerUp called for', id)
        this.snapshot.powerUps = this.snapshot.powerUps.filter((p) => p.id !== id)
    }

    setPowerUps(powerUps: RunPowerUp[]) {
        this.snapshot.powerUps = powerUps
    }

    setLifes(lifes: number) {
        this.snapshot.lifes = lifes
    }

    setShieldActive(active: boolean) {
        this.snapshot.shieldActive = active
    }

    activatePowerUp(type: PowerUpType) {
        store.setParentsInfoBannerBlock(type, { priority: 1 })

        const bump = { ...this.snapshot.runEndStats }
        bump.powerUpsCollected += 1

        if (type === 'battery') {
            bump.batteryPowerUpsCollected += 1
            bump.powerUpsCollected += 1
            this.snapshot.runEndStats = bump
            this.snapshot.batteryCount += 1
            playSound('powerUpCollect')
            playSound('batteryReady')
            return
        }

        if (type === 'shield') {
            if (!this.canCollectShield()) return
            this.lastShieldCollectTime = performance.now()
            bump.shieldPowerUpsCollected += 1
            this.snapshot.runEndStats = bump
            this.snapshot.shieldActive = true
            playSound('powerUpCollect')
            playSound('shieldCollect')
            return
        }

        if (type === 'plane') {
            bump.planePowerUpsCollected += 1
            this.snapshot.runEndStats = bump
            this.snapshot.planeTimer = planePowerUpDuration
            playCharacterPlaneSoundAfterCollect()

            // First-time plane tutorial freezes the run; start star music when
            // the overlay closes so it lines up with the power-up timer.
            if (store.tutorialEnabled && store.powerUpTutorial.willShow('plane')) {
                store.powerUpTutorial.deferStarMusic()
            } else {
                playMusicInterruption('musicStarCollect')
            }
            return
        }

        if (type === 'heart') {
            bump.heartPowerUpsCollected += 1
            bump.powerUpsCollected += 1
            this.snapshot.runEndStats = bump
            this.snapshot.lifes = Math.min(maxLifes, this.snapshot.lifes + 1)
            playCharacterHeartCollectSoundAfterCollect()
            return
        }

        if (type === 'coin') {
            bump.coinPowerUpsCollected += 1
            bump.powerUpsCollected += 1
            this.snapshot.runEndStats = bump
            playCoinCollectSound()
            return
        }
    }

    get points(): number {
        return this.snapshot.runEndStats.coinPowerUpsCollected
    }

    get topScoringPowerUp(): PowerUpType | null {
        const stats = this.snapshot.runEndStats
        return selectCardPowerUp({
            coin: stats.coinPowerUpsCollected,
            shield: stats.shieldPowerUpsCollected,
            battery: stats.batteryPowerUpsCollected,
            plane: stats.planePowerUpsCollected,
        })
    }

    consumeShield(obstacleId: string) {
        this.snapshot.shieldActive = false
        window.dispatchEvent(new Event(SHIELD_BREAK_EVENT))
        this.hideObstacle(obstacleId)
        playSound('shieldCrash')
    }

    setLaneIndex(laneIndex: number) {
        this.snapshot.laneIndex = clampLaneIndex(laneIndex)
    }

    debugSetPhase(phase: RunPhase) {
        this.phase = phase
        if (phase !== 'levelComplete') {
            this.levelCompleteCardAnimationComplete = false
        }
        if (phase !== 'endCard') {
            this.cardEpicEnabled = false
            this.getGameEndCardPressed = false
        }
    }

    setPlaneTimer(timer: number) {
        this.snapshot.planeTimer = timer
    }

    setBatteryCount(count: number) {
        this.snapshot.batteryCount = count
    }

    setSnapActive(active: boolean) {
        this.snapshot.snapActive = active
    }

    hiddenObstacleIds = new Set<string>()
    pendingObstacleHideTimeouts = new Map<string, number>()

    hideObstacle(obstacleId: string, delaySeconds = 0) {
        const existingTimeoutId = this.pendingObstacleHideTimeouts.get(obstacleId)
        if (existingTimeoutId !== undefined) {
            window.clearTimeout(existingTimeoutId)
            this.pendingObstacleHideTimeouts.delete(obstacleId)
        }

        if (delaySeconds <= 0) {
            this.hiddenObstacleIds.add(obstacleId)
            return
        }

        const timeoutId = window.setTimeout(() => {
            this.hiddenObstacleIds.add(obstacleId)
            this.pendingObstacleHideTimeouts.delete(obstacleId)
        }, delaySeconds * 1000)

        this.pendingObstacleHideTimeouts.set(obstacleId, timeoutId)
    }

    clearPendingObstacleHideTimeouts() {
        this.pendingObstacleHideTimeouts.forEach((timeoutId) => {
            window.clearTimeout(timeoutId)
        })
        this.pendingObstacleHideTimeouts.clear()
    }

    useBattery(obstacleIds: string[]) {
        if (this.snapshot.batteryCount <= 0) return
        if (obstacleIds.length === 0) return

        this.snapshot.batteryCount = Math.max(0, this.snapshot.batteryCount - 1)
        const isLastBattery = this.snapshot.batteryCount === 0
        obstacleIds.forEach((obstacleId) => {
            this.hideObstacle(obstacleId)
        })

        if (isLastBattery) {
            void playSoundUntilFinished('batteryActivate').then(() => {
                playSound('batteryDeactivate')
            })
            setTimeout(() => {
                playSound('characterLightning')
            }, 500)
            return
        }

        playCharacterLightningSoundAfterActivate()
    }

    endRunAsGameOver() {
        this.snapshot.lifes = 0
        this.levelCompleteCardAnimationComplete = false
        this.cardEpicEnabled = false
        this.getGameEndCardPressed = false
        this.phase = 'gameOver'
    }

    debugForceGameOver() {
        this.endRunAsGameOver()
    }

    debugForceLevelComplete() {
        this.completeRun()
    }

    debugClearPowerUps() {
        this.snapshot.powerUps = []
    }
}


